// Çözüm/Açıklama normalizer'ı: delimiter'sız ham LaTeX içeren metinlerde
// yalnızca matematik ifadelerini tespit edip $...$ ile sarar.
// - Mevcut $...$ / $$...$$ içeren metinler AYNEN korunur (idempotent).
// - Türkçe düz metin math mode'a sokulmaz, Türkçe karakterler korunur.
// - "\ " (backslash-space) kelime ayraçları düz metinde normal boşluğa çevrilir.

const TURKISH_RE = /[ÇĞİÖŞÜçğıöşü]/;
const DEFINITE_MATH_RE = /\\[a-zA-Z]|[\^_{}]/;
const MATH_OP_RE = /[=<>+\-*/±√]/;
const MATH_CHARSET_RE = /^[A-Za-z0-9().,+\-*/=<>\\^{}|'°÷× -]+$/;
const NEUTRAL_RE = /^[0-9().,+\-*/=<>° -]*$/;
const SINGLE_VAR_RE = /^[A-Za-z]$/;
const TRAIL_PUNCT_RE = /[.,;:!?]+$/;
const APOS_SUFFIX_RE = /'[A-Za-zÇĞİÖŞÜçğıöşü]+$/;
const WORD_EQ_CHAIN_RE = /^=?([A-Za-zÇĞİÖŞÜçğıöşü]{2,})(=.+)$/;
const MATH_MARKER_RE = /[\\^_{}=<>]/;

export function normalizeSolutionMath(text) {
  if (typeof text !== "string" || !text) return text;
  if (text.includes("$")) return text; // zaten delimiter'lı → koru (idempotent)
  if (!MATH_MARKER_RE.test(text)) return text; // matematik işareti yok → düz metin
  return text.split("\n").map(normalizeLine).join("\n");
}

function classifyCore(core) {
  if (!core) return "prose";
  if (DEFINITE_MATH_RE.test(core)) return "math";
  if (SINGLE_VAR_RE.test(core)) return "math";
  if (TURKISH_RE.test(core)) return "prose";
  if (core.includes("%")) return "prose"; // % KaTeX'te yorum satırıdır → math'a alma
  if (/[A-Za-z]{2,}/.test(core) && !/[=^_{}\\\d]/.test(core)) return "prose"; // km/sa gibi birimler
  if (MATH_OP_RE.test(core) && MATH_CHARSET_RE.test(core)) return "math";
  if (NEUTRAL_RE.test(core)) return "neutral";
  return "prose";
}

// Çok harfli {grup} içerikleri math italic yerine düz yazı dizilir: {kayık} → {\text{kayık}}
const BRACE_TEXT_RE = /\{([A-Za-zÇĞİÖŞÜçğıöşü]{2,})\}/g;
function fixMathToken(tok) {
  return tok.replace(BRACE_TEXT_RE, "{\\text{$1}}");
}

function segmentToken(tok) {
  let core = tok;
  let trail = "";
  let apos = "";
  const tm = core.match(TRAIL_PUNCT_RE);
  if (tm) { trail = tm[0]; core = core.slice(0, -trail.length); }
  const am = core.match(APOS_SUFFIX_RE);
  if (am) { apos = am[0]; core = core.slice(0, -apos.length); }
  const suffixText = apos + trail;

  const segs = [];
  // word= zinciri çözümleme: "Eğim=hız=\frac{..}" → prose(Eğim) + math(=) + prose(hız) + math(=\frac{..})
  let guard = 0;
  let wm;
  while ((wm = core.match(WORD_EQ_CHAIN_RE)) && guard++ < 5) {
    if (classifyCore(wm[2]) !== "math") break;
    if (wm[0].startsWith("=")) segs.push({ text: "=", kind: "math" });
    segs.push({ text: wm[1], kind: "prose", attach: segs.length > 0, attachNext: true });
    core = wm[2];
  }

  if (core) segs.push({ text: core, kind: classifyCore(core) });
  if (suffixText) segs.push({ text: suffixText, kind: "prose", attach: true });
  return segs;
}

function normalizeLine(line) {
  const rawTokens = line.split(/\\ |\s+/).filter(Boolean);
  if (!rawTokens.length) return line;

  let segs = [];
  for (const tok of rawTokens) segs.push(...segmentToken(tok));
  if (!segs.some((s) => s.kind === "math")) {
    // matematik yok: yalnızca "\ " ayraçlarını düzelt
    return rawTokens.join(" ");
  }

  // neutral segmentler math komşusu varsa math run'a katılır (ör. "x < 5")
  const kinds = segs.map((s) => s.kind);
  for (let i = 0; i < segs.length; i++) {
    if (kinds[i] !== "neutral") continue;
    let j = i;
    while (j + 1 < segs.length && kinds[j + 1] === "neutral") j++;
    const before = i > 0 ? kinds[i - 1] : null;
    const after = j + 1 < segs.length ? kinds[j + 1] : null;
    if (before === "math" || after === "math") {
      for (let k = i; k <= j; k++) kinds[k] = "math";
    }
    i = j;
  }

  const pieces = [];
  let mathRun = [];
  let glueNext = false; // word= split prefix'inden gelen math boşluksuz yapışır
  const flushMath = () => {
    if (!mathRun.length) return;
    pieces.push({ text: "$" + mathRun.map(fixMathToken).join(" ") + "$", spaceBefore: !glueNext });
    mathRun = [];
    glueNext = false;
  };
  for (let i = 0; i < segs.length; i++) {
    const s = { ...segs[i], kind: kinds[i] };
    if (s.kind === "math") { mathRun.push(s.text); continue; }
    flushMath();
    pieces.push({ text: s.text, spaceBefore: !s.attach });
    glueNext = !!s.attachNext;
  }
  flushMath();

  let out = "";
  for (const p of pieces) {
    if (p.spaceBefore && out) out += " ";
    out += p.text;
  }
  return out;
}
